import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";

export interface PurchaseCostTypeRecord {
  id: ID;
  /** Stable key for reporting / integrations */
  code: string;
  label: string;
  sortOrder: number;
  createdAt: string;
}

const LS_KEY = "inventory.purchaseCostTypes.v1";
const BOOT_KEY = "inventory.purchaseCostTypes.demoSeeded.v1";

type PurchaseCostTypeApiRecord = {
  id: string;
  code: string;
  label: string;
  sort_order: number;
  created_at: string;
};

type ListPurchaseCostTypesApiResponse = {
  data?: PurchaseCostTypeApiRecord[];
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
  };
};

type UpsertPurchaseCostTypeApiResponse = {
  message?: string;
  data?: PurchaseCostTypeApiRecord;
};

type DeletePurchaseCostTypeApiResponse = {
  message?: string;
};

type UpsertPurchaseCostTypeResult = {
  record: PurchaseCostTypeRecord;
  message: string;
};

type DeletePurchaseCostTypeResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: PurchaseCostTypeRecord[]): PurchaseCostTypeRecord[] {
  const byId = new Map<ID, PurchaseCostTypeRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function mapApiRecord(record: PurchaseCostTypeApiRecord): PurchaseCostTypeRecord {
  return {
    id: record.id,
    code: record.code,
    label: record.label,
    sortOrder: record.sort_order,
    createdAt: record.created_at,
  };
}

function load(): PurchaseCostTypeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PurchaseCostTypeRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: PurchaseCostTypeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listPurchaseCostTypeRecords(): PurchaseCostTypeRecord[] {
  return load().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
}

export function getPurchaseCostTypeById(id: ID): PurchaseCostTypeRecord | undefined {
  return load().find((t) => t.id === id);
}

export function codeExists(code: string, exceptId?: ID): boolean {
  const c = slugify(code);
  return load().some((t) => slugify(t.code) === c && t.id !== exceptId);
}

export async function syncPurchaseCostTypesFromApi() {
  const response = await api.get<ListPurchaseCostTypesApiResponse>("/api/v1/catalog/purchase-cost-types", {
    params: {
      page: 1,
      per_page: 200,
    },
  });
  const records = (response.data?.data ?? []).map(mapApiRecord);
  save(records);
  return listPurchaseCostTypeRecords();
}

export async function createPurchaseCostType(data: {
  code: string;
  label: string;
  sortOrder?: number;
}): Promise<UpsertPurchaseCostTypeResult> {
  const items = load();
  const payload = {
    code: slugify(data.code),
    label: data.label.trim(),
    sort_order: data.sortOrder ?? 0,
  };
  const response = await api.post<UpsertPurchaseCostTypeApiResponse>("/api/v1/catalog/purchase-cost-types", payload);
  const created = response.data?.data;
  if (!created) {
    throw new Error("Invalid create purchase cost type response.");
  }
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.push(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Purchase cost type created successfully.",
  };
}

export async function updatePurchaseCostType(
  id: ID,
  data: { code: string; label: string; sortOrder?: number },
): Promise<UpsertPurchaseCostTypeResult | null> {
  const items = load();
  const i = items.findIndex((t) => t.id === id);
  if (i === -1) return null;
  const payload = {
    code: slugify(data.code) || items[i].code,
    label: data.label.trim(),
    sort_order: data.sortOrder ?? items[i].sortOrder,
  };
  const response = await api.put<UpsertPurchaseCostTypeApiResponse>(`/api/v1/catalog/purchase-cost-types/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update purchase cost type response.");
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Purchase cost type updated successfully.",
  };
}

export async function deletePurchaseCostType(id: ID): Promise<DeletePurchaseCostTypeResult> {
  const response = await api.delete<DeletePurchaseCostTypeApiResponse>(`/api/v1/catalog/purchase-cost-types/${id}`);
  const items = load();
  const nextItems = items.filter((t) => t.id !== id);
  const deleted = nextItems.length !== items.length;
  if (deleted) {
    save(nextItems);
  }
  return {
    success: deleted,
    message: response.data?.message || "Purchase cost type deleted successfully.",
  };
}

export function replaceAllPurchaseCostTypes(records: PurchaseCostTypeRecord[]) {
  save(records);
}

/** Seed default breakdown types when storage is empty. */
export function seedPurchaseCostTypesIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(BOOT_KEY, "1");
    return;
  }
  const now = new Date().toISOString();
  const seed: PurchaseCostTypeRecord[] = [
    { id: "pct_custom_duty", code: "custom_duty", label: "Custom duty", sortOrder: 1, createdAt: now },
    { id: "pct_freight", code: "freight", label: "Freight / shipping", sortOrder: 2, createdAt: now },
    { id: "pct_documentation", code: "documentation", label: "Documentation", sortOrder: 3, createdAt: now },
    { id: "pct_repairing", code: "repairing_cost", label: "Repairing cost", sortOrder: 4, createdAt: now },
  ];
  save(seed);
  localStorage.setItem(BOOT_KEY, "1");
}
